import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Ticket, CheckCircle2, Clock, Search, Users } from 'lucide-react';
import {
  subscriptionService,
  type VoucherRow,
  type CreateVoucherInput,
} from '../../services/subscriptionService';
import { useLanguage } from '../../features/language/useLanguage';
import { useToast } from '../../components/common/Toast';
import { SegmentedToggle } from '../../components/common/SegmentedToggle';
import { Pagination } from '../../components/common/Pagination';
import { usePaginationState } from '../../hooks/usePaginationState';
import { VoucherStatCard } from './components/vouchers/VoucherStatCard';
import { VoucherTable } from './components/vouchers/VoucherTable';
import { CreateVoucherModal } from './components/vouchers/CreateVoucherModal';
import { VoucherDetailDrawer } from './components/vouchers/VoucherDetailDrawer';
import { DeleteVoucherDialog } from './components/vouchers/DeleteVoucherDialog';

type StatusFilter = 'all' | VoucherRow['status'];
const STATUS_FILTERS: StatusFilter[] = ['all', 'active', 'expired', 'exhausted', 'deleted'];

export default function VouchersSettings() {
  const { t } = useLanguage();
  const { showToast } = useToast();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>('active');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [openVoucherId, setOpenVoucherId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<VoucherRow | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const { page, size, offset, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });
  resetOnChange([filter, search]);

  const handleCopyCode = async (e: React.MouseEvent, code: string) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1500);
    } catch {
      /* ignore */
    }
  };

  const list = useQuery({
    queryKey: ['admin', 'vouchers'],
    queryFn: subscriptionService.adminListVouchers,
  });

  const create = useMutation({
    mutationFn: (input: CreateVoucherInput) => subscriptionService.adminCreateVoucher(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'vouchers'] });
      setShowCreate(false);
      showToast(t('subscription.voucher.create_success'), 'success');
    },
    onError: (err: unknown) => {
      const apiErr = err as { response?: { data?: { message?: string } } };
      showToast(apiErr?.response?.data?.message ?? t('subscription.voucher.create_error'), 'error');
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => subscriptionService.adminDeleteVoucher(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'vouchers'] });
      setConfirmDelete(null);
      showToast(t('subscription.voucher.delete_success'), 'success');
    },
    onError: (err: unknown) => {
      const apiErr = err as { response?: { data?: { message?: string } } };
      showToast(apiErr?.response?.data?.message ?? t('subscription.voucher.delete_error'), 'error');
    },
  });

  const filterLabels: Record<StatusFilter, string> = {
    all: t('subscription.voucher.filter.all'),
    active: t('subscription.voucher.filter.active'),
    expired: t('subscription.voucher.filter.expired'),
    exhausted: t('subscription.voucher.filter.exhausted'),
    deleted: t('subscription.voucher.filter.deleted'),
  };

  const stats = useMemo(() => {
    const data = list.data ?? [];
    return {
      total: data.length,
      active: data.filter((v) => v.status === 'active').length,
      expired: data.filter((v) => v.status === 'expired').length,
      exhausted: data.filter((v) => v.status === 'exhausted').length,
    };
  }, [list.data]);

  const rows = useMemo(() => {
    const data = list.data ?? [];
    const s = search.trim().toLowerCase();
    return data.filter((v) => {
      if (filter !== 'all' && v.status !== filter) return false;
      if (s && !v.code.toLowerCase().includes(s) && !v.name.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [list.data, filter, search]);

  const total = rows.length;
  const paginatedRows = useMemo(() => rows.slice(offset, offset + size), [rows, offset, size]);

  return (
    <div className="space-y-6" data-test-id="vouchers-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('settings.vouchers')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('subscription.voucher.page_subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-test-id="create-voucher"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium shadow-sm transition-colors"
        >
          <Plus className="h-4 w-4" />
          {t('subscription.voucher.create_cta')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <VoucherStatCard icon={<Ticket className="h-4 w-4" />} label={t('subscription.voucher.stats.total')} value={stats.total} tone="neutral" />
        <VoucherStatCard icon={<CheckCircle2 className="h-4 w-4" />} label={t('subscription.voucher.stats.active')} value={stats.active} tone="success" />
        <VoucherStatCard icon={<Clock className="h-4 w-4" />} label={t('subscription.voucher.stats.expired')} value={stats.expired} tone="warning" />
        <VoucherStatCard icon={<Users className="h-4 w-4" />} label={t('subscription.voucher.stats.exhausted')} value={stats.exhausted} tone="info" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <SegmentedToggle
          ariaLabel={t('subscription.voucher.col.status')}
          value={filter}
          onChange={setFilter}
          minTabWidth="84px"
          testIdPrefix="voucher-filter"
          items={STATUS_FILTERS.map((f) => ({ key: f, label: filterLabels[f] }))}
        />
        <div className="relative w-full lg:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('subscription.voucher.search_placeholder')}
            data-test-id="voucher-search-input"
            className="w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {list.isLoading ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500">{t('common.loading')}</div>
        ) : total === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 mb-3">
              <Ticket className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">{t('subscription.voucher.list_empty')}</p>
          </div>
        ) : (
          <VoucherTable
            rows={paginatedRows}
            copiedCode={copiedCode}
            onCopyCode={handleCopyCode}
            onOpen={setOpenVoucherId}
            onDelete={setConfirmDelete}
          />
        )}
        <Pagination
          page={page}
          pageSize={size}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          isLoading={list.isLoading}
        />
      </div>

      {showCreate && (
        <CreateVoucherModal
          onClose={() => setShowCreate(false)}
          onSubmit={(input) => create.mutate(input)}
          isPending={create.isPending}
        />
      )}
      {openVoucherId && <VoucherDetailDrawer id={openVoucherId} onClose={() => setOpenVoucherId(null)} />}
      {confirmDelete && (
        <DeleteVoucherDialog
          voucher={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => del.mutate(confirmDelete.id)}
          isPending={del.isPending}
        />
      )}
    </div>
  );
}
